import {useCallback, useMemo} from 'react'
import {useSearchParams} from 'react-router-dom'

export const ACCESS_TAB_PARAM = 'accessTab'
export const LIFECYCLE_TAB_PARAM = 'lifecycleTab'

export const ACCESS_TAB_ASSIGNMENTS = 'assignments'
export const ACCESS_TAB_TIMELINE = 'timeline'
export const LIFECYCLE_TAB_OVERVIEW = 'overview'
export const LIFECYCLE_TAB_LIFECYCLE = 'lifecycle'

export type AccessTab = typeof ACCESS_TAB_ASSIGNMENTS | typeof ACCESS_TAB_TIMELINE
export type LifecycleTab = typeof LIFECYCLE_TAB_OVERVIEW | typeof LIFECYCLE_TAB_LIFECYCLE

const ACCESS_TAB_VALUES: ReadonlySet<AccessTab> = new Set([ACCESS_TAB_ASSIGNMENTS, ACCESS_TAB_TIMELINE])
const LIFECYCLE_TAB_VALUES: ReadonlySet<LifecycleTab> = new Set([LIFECYCLE_TAB_OVERVIEW, LIFECYCLE_TAB_LIFECYCLE])

function parseAccessTab(raw: string | null): AccessTab {
    return raw && ACCESS_TAB_VALUES.has(raw as AccessTab) ? (raw as AccessTab) : ACCESS_TAB_ASSIGNMENTS
}

function parseLifecycleTab(raw: string | null): LifecycleTab {
    return raw && LIFECYCLE_TAB_VALUES.has(raw as LifecycleTab) ? (raw as LifecycleTab) : LIFECYCLE_TAB_OVERVIEW
}

export interface UseIdentityDetailTabResult {
    accessTab: AccessTab
    lifecycleTab: LifecycleTab
    setAccessTab: (next: AccessTab) => void
    setLifecycleTab: (next: LifecycleTab) => void
}

/**
 * URL-state hook for the two embedded tab groups on the IdentityDetail page.
 *
 * The page has two independent tab clusters — one nested under the Access
 * Profiles card (assignments / timeline), one under the Lifecycle card
 * (overview / lifecycle). Each cluster persists its active tab through a
 * dedicated query parameter so a refresh / deep-link recovers state.
 *
 * - {@code ?accessTab=assignments|timeline}
 * - {@code ?lifecycleTab=overview|lifecycle}
 *
 * Unknown / missing values resolve to the default tab. Setting a tab to its
 * default removes the parameter so URLs stay clean. Other search params on
 * the URL are preserved.
 */
export function useIdentityDetailTab(): UseIdentityDetailTabResult {
    const [searchParams, setSearchParams] = useSearchParams()

    const accessTab = useMemo(
        () => parseAccessTab(searchParams.get(ACCESS_TAB_PARAM)),
        [searchParams],
    )

    const lifecycleTab = useMemo(
        () => parseLifecycleTab(searchParams.get(LIFECYCLE_TAB_PARAM)),
        [searchParams],
    )

    const setAccessTab = useCallback((next: AccessTab) => {
        setSearchParams((prev) => {
            const out = new URLSearchParams(prev)
            if (next === ACCESS_TAB_ASSIGNMENTS) {
                out.delete(ACCESS_TAB_PARAM)
            } else {
                out.set(ACCESS_TAB_PARAM, next)
            }
            return out
        }, {replace: true})
    }, [setSearchParams])

    const setLifecycleTab = useCallback((next: LifecycleTab) => {
        setSearchParams((prev) => {
            const out = new URLSearchParams(prev)
            if (next === LIFECYCLE_TAB_OVERVIEW) {
                out.delete(LIFECYCLE_TAB_PARAM)
            } else {
                out.set(LIFECYCLE_TAB_PARAM, next)
            }
            return out
        }, {replace: true})
    }, [setSearchParams])

    return {accessTab, lifecycleTab, setAccessTab, setLifecycleTab}
}
